import { internalFetch } from '../utils/fetchHelper';

export type WorkStatusGroupAudience = 'Client' | 'Manager' | 'Contractor';
export type StatusGroupKind = 'work' | 'order';

const statusGroupEndpoint = (kind: StatusGroupKind): string =>
  kind === 'order' ? 'orderStatusGroup' : 'workStatusGroup';

const statusGroupsEndpoint = (kind: StatusGroupKind): string =>
  kind === 'order' ? 'orderStatusGroups' : 'workStatusGroups';

const withApiKey = (pathWithQuery: string): string => {
  const apiKey = import.meta.env.VITE_BACKEND_API_KEY;
  if (!apiKey || apiKey === 'undefined') {
    return pathWithQuery;
  }
  const separator = pathWithQuery.includes('?') ? '&' : '?';
  return `${pathWithQuery}${separator}code=${encodeURIComponent(apiKey)}`;
};

export interface IWorkStatusGroup {
  id: number;
  statusId?: number;
  audience: WorkStatusGroupAudience;
  name: string;
  code?: string;
  color?: string;
  icon?: string;
  showInDashboardCard?: boolean | number | null;
  dashboardCardNumber?: number | null;
  isDeleted?: boolean | null;
}

export interface ICreateWorkStatusGroupRequest {
  statusId: number;
  audience: WorkStatusGroupAudience;
  name: string;
  code?: string;
  color?: string;
  icon?: string;
  showInDashboardCard?: boolean | number | null;
  dashboardCardNumber?: number | null;
}

export interface ICreateWorkStatusGroupResponse {
  workStatusGroupId: number;
  isSuccess: boolean;
  message: string | null;
  correlationId?: string;
}

export interface IUpdateWorkStatusGroupRequest {
  statusId: number;
  audience: WorkStatusGroupAudience;
  name: string;
  code?: string;
  color?: string;
  icon?: string;
  showInDashboardCard?: boolean | number | null;
  dashboardCardNumber?: number | null;
  isDeleted: boolean;
}

export const getWorkStatusGroups = async (
  abortController: AbortController,
  audience: WorkStatusGroupAudience,
  kind: StatusGroupKind = 'work'
): Promise<IWorkStatusGroup[]> => {
  return internalFetch(
    withApiKey(
      `${import.meta.env.VITE_BACKEND_API_URL}/p/${statusGroupsEndpoint(
        kind
      )}?audience=${encodeURIComponent(audience)}`
    ),
    'GET',
    abortController
  );
};

export const createWorkStatusGroup = async (
  abortController: AbortController,
  item: ICreateWorkStatusGroupRequest,
  kind: StatusGroupKind = 'work'
): Promise<ICreateWorkStatusGroupResponse> => {
  return internalFetch(
    withApiKey(
      `${import.meta.env.VITE_BACKEND_API_URL}/p/${statusGroupEndpoint(kind)}`
    ),
    'POST',
    abortController,
    JSON.stringify(item)
  );
};

export const updateWorkStatusGroup = async (
  abortController: AbortController,
  workStatusGroupId: number,
  item: IUpdateWorkStatusGroupRequest,
  kind: StatusGroupKind = 'work'
): Promise<number> => {
  return internalFetch(
    withApiKey(
      `${import.meta.env.VITE_BACKEND_API_URL}/p/${statusGroupEndpoint(
        kind
      )}?workStatusGroupId=${workStatusGroupId}`
    ),
    'PUT',
    abortController,
    JSON.stringify(item)
  );
};

export const deleteWorkStatusGroup = async (
  abortController: AbortController,
  workStatusGroupId: number,
  kind: StatusGroupKind = 'work'
): Promise<number> => {
  return internalFetch(
    withApiKey(
      `${import.meta.env.VITE_BACKEND_API_URL}/p/${statusGroupEndpoint(
        kind
      )}?workStatusGroupId=${workStatusGroupId}`
    ),
    'DELETE',
    abortController
  );
};
